/* NowcastNext30Min — compact "next 30 min" mini-timeline.
 *
 * Only renders when the optical-flow nowcast says rain is actually inbound.
 * Two-line layout (matches the brief):
 *   Row 1: icon + "Peak in 12 min · arriving from SW at 28 km/h" + "48%"
 *   Row 2: full-width sparkline (7 bars, one per 5-min step)
 *
 * Sized to ~64 px tall — slots cleanly between RainConfidenceBar and the
 * verdict headline in VerdictPage.
 */
import { motion } from "framer-motion";
import { CloudRain, NavigationArrow } from "@phosphor-icons/react";

const COMPASS_8 = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

function compass(bearing) {
  if (!Number.isFinite(bearing)) return null;
  const idx = Math.round(bearing / 45) % 8;
  return COMPASS_8[idx];
}

function FullWidthBars({ timeline, accent, mutedColor }) {
  if (!timeline?.length) return null;
  const peak = Math.max(1, ...timeline.map((t) => t.intensity || 0));
  return (
    <div
      className="w-full flex items-end gap-[3px] h-3.5"
      aria-hidden
      data-testid="nowcast-bars"
    >
      {timeline.map((t, i) => {
        const h = Math.max(2, Math.round((t.intensity / peak) * 14));
        const isFirst = i === 0;
        const peakIdx = timeline.reduce(
          (best, cur, ci) => (cur.intensity > timeline[best].intensity ? ci : best),
          0,
        );
        const isPeak = i === peakIdx;
        return (
          <span
            key={i}
            className="flex-1 transition-all"
            style={{
              height: h,
              borderRadius: 2,
              background: isFirst ? mutedColor : accent,
              opacity: isFirst ? 0.5 : 0.4 + (t.intensity / peak) * 0.6,
              boxShadow: isPeak ? `0 0 8px ${accent}aa` : "none",
            }}
          />
        );
      })}
    </div>
  );
}

export default function NowcastNext30Min({ data, theme }) {
  if (!data) return null;
  const peakMin = data.peak_minute;
  const peakIntensity = Math.round(data.peak_intensity || 0);
  const motionData = data.storm_motion || {};
  const fromCompass = compass(motionData.bearing_deg);
  const speed = Math.round(motionData.speed_kmh || 0);

  const peakStr = peakMin === 0 ? "Already peaking" : `Peak in ${peakMin} min`;
  const motionStr =
    speed >= 4 && fromCompass
      ? `arriving from ${fromCompass} at ${speed} km/h`
      : speed >= 4
        ? `${speed} km/h drift`
        : "slow-moving cell";

  return (
    <motion.div
      className="w-full rounded-2xl px-3.5 py-2"
      style={{
        background: theme.chip,
        border: `1px solid ${theme.surfaceBorder}`,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
      data-testid="nowcast-next-30"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.12 }}
    >
      {/* Row 1 — icon + main copy + peak % */}
      <div className="flex items-center gap-2.5">
        <span
          className="shrink-0 w-7 h-7 rounded-xl flex items-center justify-center"
          style={{ background: `${theme.accent}22`, color: theme.accent }}
        >
          <CloudRain size={14} weight="duotone" />
        </span>

        <div className="flex-1 min-w-0">
          <div
            className="text-[12px] font-semibold leading-tight truncate"
            style={{ color: theme.textPrimary }}
            data-testid="nowcast-headline"
          >
            {peakStr}
            <span style={{ color: theme.textMuted, fontWeight: 500 }}>
              {" · "}
              {motionStr}
            </span>
          </div>
          <div
            className="text-[9px] uppercase tracking-[0.18em] font-semibold leading-tight mt-0.5"
            style={{ color: theme.textMuted }}
          >
            Optical-flow · next 30 min
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-1.5">
          {speed >= 4 && Number.isFinite(motionData.bearing_deg) && (
            <motion.span
              className="inline-flex"
              initial={{ rotate: motionData.bearing_deg }}
              animate={{ rotate: motionData.bearing_deg }}
              transition={{ type: "spring", stiffness: 110, damping: 18 }}
              style={{
                color: theme.accent,
                transformOrigin: "50% 50%",
              }}
            >
              <NavigationArrow size={12} weight="fill" />
            </motion.span>
          )}
          <span
            className="tabular-nums font-mono text-[13px] font-bold"
            style={{ color: theme.accent }}
            data-testid="nowcast-peak"
          >
            {peakIntensity}%
          </span>
        </div>
      </div>

      {/* Row 2 — full-width sparkline */}
      <div className="mt-1.5">
        <FullWidthBars
          timeline={data.timeline}
          accent={theme.accent}
          mutedColor={theme.textMuted}
        />
      </div>
    </motion.div>
  );
}
